import React, { FC, useState, useCallback, useRef, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { TaskType, TaskChatMessage, TaskChatSSEEvent } from '@dreamai/types';
import { ReactComponent as ArrowCircleRight } from '../../../icons/arrow-right-circle.svg';
import { generateMessageColors } from '../../chat/sidebar/threadUtils';
import './task-modal.scss';

const MAX_USER_MESSAGES = 50;
const MAX_MESSAGE_LENGTH = 500;

interface TaskOption {
  emoji: string;
  title: string;
  description: string;
  requiresPhysical: boolean;
  taskType: TaskType;
}

const TASK_OPTIONS: TaskOption[] = [
  {
    emoji: '☕',
    title: 'Need a caffeine hit?',
    description: "I'll bring a coffee or tea to your door, my treat",
    requiresPhysical: true,
    taskType: 'coffee',
  },
  {
    emoji: '🌷',
    title: 'Brighten up your home',
    description: 'Get some flowers delivered to your door, on me.',
    requiresPhysical: true,
    taskType: 'flowers',
  },
  {
    emoji: '🎤',
    title: 'No plans tonight? Not anymore',
    description: "I'll book you tickets to a film nearby, gratis.",
    requiresPhysical: false,
    taskType: 'cinema',
  },
  {
    emoji: '🪴',
    title: 'Something for your office',
    description: 'Something lacking in your workspace? Let me brighten it up.',
    requiresPhysical: false,
    taskType: 'gift',
  },
];

// ── SSE streaming helper ──────────────────────────────────────────────

async function streamTaskChat(
  token: string,
  body: {
    taskType: TaskType;
    messages: TaskChatMessage[];
    assistantName?: string;
    userName?: string;
  },
  onToken: (text: string) => void,
  onDone: (readyToConfirm: boolean, summary: string | null) => void,
  onError: (error: string) => void,
): Promise<void> {
  const baseUrl = process.env.REACT_APP_API_URL || '';
  const res = await fetch(`${baseUrl}/onboarding/task-chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!res.ok || !res.body) {
    onError('Failed to start chat stream');
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  let reading = true;
  while (reading) {
    const { done, value } = await reader.read();
    if (done) {
      reading = false;
      break;
    }
    buffer += decoder.decode(value, { stream: true });

    // Parse SSE lines
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      try {
        const event: TaskChatSSEEvent = JSON.parse(line.slice(6));
        if (event.type === 'token') {
          onToken(event.content);
        } else if (event.type === 'done') {
          onDone(event.readyToConfirm, event.summary);
        } else if (event.type === 'error') {
          onError(event.error);
        }
      } catch {
        // skip malformed SSE lines
      }
    }
  }
}

// ── Colour helpers ────────────────────────────────────────────────────

function assistantBubbleStyle(
  colour: string | null,
): React.CSSProperties | undefined {
  if (!colour) return undefined;
  const { background, borderColor, boxShadow } = generateMessageColors(colour);
  return {
    background,
    borderColor,
    boxShadow,
    borderWidth: 0,
    borderStyle: 'solid',
    borderRadius: 8,
  };
}

// ── Component ─────────────────────────────────────────────────────────

interface TaskModalProps {
  areaSupported: boolean;
  assistantName: string;
  assistantPredominantColour: string | null;
  userName: string;
  onComplete: (
    taskType: TaskType,
    summary: string | null,
    messages: TaskChatMessage[],
  ) => void;
  onSkip: () => void;
}

const TaskModal: FC<TaskModalProps> = ({
  areaSupported,
  assistantName,
  assistantPredominantColour,
  userName,
  onComplete,
  onSkip,
}) => {
  const { getAccessTokenSilently } = useAuth0();

  // ── Selection phase state ─────────────────────────────────────
  const [notHome, setNotHome] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // ── Chat phase state ──────────────────────────────────────────
  const [chatActive, setChatActive] = useState(false);
  const [activeTaskType, setActiveTaskType] = useState<TaskType | null>(null);
  const [messages, setMessages] = useState<TaskChatMessage[]>([]);
  const [streamingText, setStreamingText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [readyToConfirm, setReadyToConfirm] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // ── Character drip buffer for smooth streaming ─────────────────
  const streamBufferRef = useRef('');
  const displayedLenRef = useRef(0);
  const dripTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startDrip = useCallback(() => {
    if (dripTimerRef.current) return;
    displayedLenRef.current = 0;
    dripTimerRef.current = setInterval(() => {
      const buf = streamBufferRef.current;
      const cur = displayedLenRef.current;
      if (cur < buf.length) {
        const charsToAdd = Math.min(2, buf.length - cur);
        displayedLenRef.current = cur + charsToAdd;
        setStreamingText(buf.slice(0, displayedLenRef.current));
      }
    }, 15);
  }, []);

  const stopDrip = useCallback(() => {
    if (dripTimerRef.current) {
      clearInterval(dripTimerRef.current);
      dripTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopDrip();
  }, [stopDrip]);

  const physicalDisabled = !areaSupported || notHome;

  // Auto-scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingText]);

  // ── Start the chat after option selection ─────────────────────

  const startChat = useCallback(
    async (taskType: TaskType) => {
      setChatActive(true);
      setActiveTaskType(taskType);
      setIsStreaming(true);
      setStreamingText('');

      try {
        const token = await getAccessTokenSilently();
        streamBufferRef.current = '';
        startDrip();

        await streamTaskChat(
          token,
          { taskType, messages: [], assistantName, userName },
          (text) => {
            streamBufferRef.current += text;
          },
          (ready, sum) => {
            stopDrip();
            const cleanText = streamBufferRef.current
              .replace(/\[\[READY\|.*?\]\]\s*$/, '')
              .trimEnd();
            setMessages([{ role: 'assistant', content: cleanText }]);
            setStreamingText('');
            setIsStreaming(false);
            if (ready) {
              setReadyToConfirm(true);
              setSummary(sum);
            }
          },
          (err) => {
            console.error('[TaskChat] Stream error:', err);
            stopDrip();
            setMessages([
              {
                role: 'assistant',
                content: "Sorry, something went wrong. Let's try that again.",
              },
            ]);
            setStreamingText('');
            setIsStreaming(false);
          },
        );
      } catch (err) {
        console.error('[TaskChat] Failed to start:', err);
        stopDrip();
        setIsStreaming(false);
      }
    },
    [getAccessTokenSilently, assistantName, userName, startDrip, stopDrip],
  );

  // ── Send a user message ───────────────────────────────────────

  const userMessageCount = messages.filter((m) => m.role === 'user').length;
  const canSendMessage = userMessageCount < MAX_USER_MESSAGES;

  const sendMessage = useCallback(async () => {
    const text = inputValue.trim();
    if (!text || isStreaming || !activeTaskType || !canSendMessage) return;

    const updatedMessages: TaskChatMessage[] = [
      ...messages,
      { role: 'user', content: text },
    ];
    setMessages(updatedMessages);
    setInputValue('');
    setIsStreaming(true);
    setStreamingText('');

    try {
      const token = await getAccessTokenSilently();
      streamBufferRef.current = '';
      startDrip();

      await streamTaskChat(
        token,
        {
          taskType: activeTaskType,
          messages: updatedMessages,
          assistantName,
          userName,
        },
        (chunk) => {
          streamBufferRef.current += chunk;
        },
        (ready, sum) => {
          stopDrip();
          const cleanText = streamBufferRef.current
            .replace(/\[\[READY\|.*?\]\]\s*$/, '')
            .trimEnd();
          setMessages((prev) => [
            ...prev,
            { role: 'assistant', content: cleanText },
          ]);
          setStreamingText('');
          setIsStreaming(false);
          if (ready) {
            setReadyToConfirm(true);
            setSummary(sum);
          }
        },
        (err) => {
          console.error('[TaskChat] Stream error:', err);
          stopDrip();
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: "Sorry, something went wrong. Let's try that again.",
            },
          ]);
          setStreamingText('');
          setIsStreaming(false);
        },
      );
    } catch (err) {
      console.error('[TaskChat] Send failed:', err);
      stopDrip();
      setIsStreaming(false);
    }
  }, [
    inputValue,
    isStreaming,
    activeTaskType,
    canSendMessage,
    messages,
    getAccessTokenSilently,
    assistantName,
    userName,
    startDrip,
    stopDrip,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    },
    [sendMessage],
  );

  // ── Selection phase handlers ──────────────────────────────────

  const handleOptionClick = (index: number) => {
    const option = TASK_OPTIONS[index];
    if (option.requiresPhysical && physicalDisabled) return;
    setSelectedIndex(selectedIndex === index ? null : index);
  };

  const handleConfirmOption = (index: number) => {
    const option = TASK_OPTIONS[index];
    startChat(option.taskType);
  };

  const handleChangeChoice = () => {
    setChatActive(false);
    setActiveTaskType(null);
    setMessages([]);
    setStreamingText('');
    setIsStreaming(false);
    setReadyToConfirm(false);
    setSummary(null);
    setInputValue('');
    setSelectedIndex(null);
  };

  // ── Render ────────────────────────────────────────────────────

  // Strip the READY marker from streaming text for display
  const displayStreamingText = streamingText
    .replace(/\[\[READY\|.*?\]\]\s*$/, '')
    .trimEnd();

  return (
    <div className='task-modal-backdrop'>
      <div className={`task-modal${chatActive ? ' task-modal-chat' : ''}`}>
        <h2 className='task-modal-title'>Let me do something for you</h2>

        <div className='task-modal-description'>
          <p>
            Between me and my team we can do tasks for you online, and in the
            real world (well, London for now).
          </p>
          <p>This is what I can do for you in the next 60 minutes...</p>
        </div>

        {/* Area banner or not-home checkbox (hidden during chat) */}
        {!chatActive &&
          (areaSupported ? (
            <button
              type='button'
              className={`not-home-banner${notHome ? ' checked' : ''}`}
              onClick={() => {
                setNotHome(!notHome);
                setSelectedIndex(null);
              }}
            >
              <div className='not-home-row'>
                <div className={`check-box${notHome ? ' checked' : ''}`}>
                  {notHome && (
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='3'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    >
                      <polyline points='20 6 9 17 4 12' />
                    </svg>
                  )}
                </div>
                <span className='not-home-label'>
                  🏠 I'm not home right now
                </span>
              </div>
              {notHome && (
                <span className='not-home-subtext'>
                  That's ok, let me do something else for you
                </span>
              )}
            </button>
          ) : (
            <div className='unsupported-banner'>
              Some options aren't available, but I can still give you a treat.
            </div>
          ))}

        {chatActive ? (
          <>
            <button
              type='button'
              className='change-choice-button'
              onClick={handleChangeChoice}
            >
              <svg
                width='16'
                height='16'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <line x1='19' y1='12' x2='5' y2='12' />
                <polyline points='12 19 5 12 12 5' />
              </svg>
              Change choice
            </button>
            <div className='chat-messages'>
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`chat-bubble chat-bubble-${msg.role}`}
                  style={
                    msg.role === 'assistant'
                      ? assistantBubbleStyle(assistantPredominantColour)
                      : undefined
                  }
                >
                  <p>{msg.content}</p>
                </div>
              ))}
              {isStreaming && displayStreamingText && (
                <div
                  className='chat-bubble chat-bubble-assistant'
                  style={assistantBubbleStyle(assistantPredominantColour)}
                >
                  <p>{displayStreamingText}</p>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            <div className='chat-input-wrapper'>
              <textarea
                className='chat-input'
                value={inputValue}
                maxLength={MAX_MESSAGE_LENGTH}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  canSendMessage
                    ? 'Type your response...'
                    : 'Message limit reached'
                }
                rows={1}
                disabled={isStreaming || isSubmitting || !canSendMessage}
              />
              <button
                type='button'
                className='chat-send-button'
                onClick={sendMessage}
                disabled={
                  isStreaming ||
                  isSubmitting ||
                  !inputValue.trim() ||
                  !canSendMessage
                }
              >
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <line x1='12' y1='19' x2='12' y2='5' />
                  <polyline points='5 12 12 5 19 12' />
                </svg>
              </button>
            </div>

            {readyToConfirm && (
              <div className='chat-footer-actions'>
                <button
                  type='button'
                  className='confirm-button'
                  disabled={isSubmitting}
                  onClick={() => {
                    setIsSubmitting(true);
                    onComplete(activeTaskType!, summary, messages);
                  }}
                >
                  {isSubmitting ? 'Placing order...' : 'Order this now'}
                  {!isSubmitting && <ArrowCircleRight />}
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Options */}
            <div className='task-options'>
              {TASK_OPTIONS.map((option, index) => {
                const disabled = option.requiresPhysical && physicalDisabled;
                const selected = selectedIndex === index;

                return (
                  <button
                    key={index}
                    type='button'
                    className={`task-option${disabled ? ' disabled' : ''}${selected ? ' selected' : ''}`}
                    onClick={() => handleOptionClick(index)}
                    disabled={disabled}
                  >
                    <div className={`check-box${selected ? ' checked' : ''}`}>
                      {selected && (
                        <svg
                          width='16'
                          height='16'
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth='3'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        >
                          <polyline points='20 6 9 17 4 12' />
                        </svg>
                      )}
                    </div>
                    <div className='task-option-text'>
                      <span className='task-option-title'>
                        {option.emoji} {option.title}
                      </span>
                      <span className='task-option-desc'>
                        {option.description}
                      </span>
                    </div>
                    {selected && (
                      <button
                        type='button'
                        className='confirm-button'
                        onClick={(e) => {
                          e.stopPropagation();
                          handleConfirmOption(index);
                        }}
                      >
                        Let's do it
                        <ArrowCircleRight />
                      </button>
                    )}
                  </button>
                );
              })}
            </div>

            <button type='button' className='skip-button' onClick={onSkip}>
              Skip
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default TaskModal;
